import { FC } from 'react';
import * as yup from 'yup';
import { useMutation } from 'react-query';
import { Dialog, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { Form, Formik } from 'formik';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { putGenerativeLabels } from 'entities/generative-models/api';
import { InputField } from 'shared/ui/InputField';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Paper } from 'shared/ui/Paper';
import { Loader } from 'shared/ui/Loader';
import { Button } from 'shared/ui/Button';
import { GenerativeLabelsType } from 'entities/Files/types';
import { getErrorMessage } from 'shared/helpers/getErrorMessage';
import { useFileEditorStore } from 'store/file-editor';

interface ManualModalPropsType {
	labelsDetails?: GenerativeLabelsType & {
		labelIndex: number;
	};
	open: boolean;
	handleClose: () => void;
}
const GenerativeLabelValidationSchema = yup.object({
	name: yup
		.string()
		.required('files.research_required')
		.matches(/^[A-Za-zА-Яа-я0-9_]+$/, 'shared_texts.no_spaces'),
	prompt: yup.string(),
});

const GenerativeLabelsModal: FC<ManualModalPropsType> = ({ labelsDetails, open, handleClose }) => {
	const { fileId } = useParams();
	const { t } = useTranslation();
	const { setFileDetails, fileDetails } = useFileEditorStore();

	const { mutate: putGenerativeLabelsMutate, isLoading: patchIsLoading } = useMutation({
		mutationKey: ['put/generative-file-labels'],
		mutationFn: putGenerativeLabels,
		onSuccess: (resposne) => {
			setFileDetails(resposne.data);
			handleClose();
		},
		onError: (err) => {
			const errorMessage = getErrorMessage(err);
			enqueueSnackbar({ message: errorMessage, variant: 'error' });
		},
	});

	const handleManualModalSubmit = (label: GenerativeLabelsType) => {
		if (fileId && fileDetails) {
			const labels = labelsDetails
				? fileDetails.labels.map((item, idx) => (idx === labelsDetails.labelIndex ? label : item))
				: [...fileDetails.labels, label];
			putGenerativeLabelsMutate({ generativeModelFileId: fileId, data: { labels, execute: false } });
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			PaperComponent={Paper}
			slotProps={{ paper: { title: t('files.add_searches') as string } }}
		>
			{patchIsLoading ? (
				<Loader />
			) : (
				<>
					<Typography variant={'body1'} color={ColorPalette.baliHai} sx={{ marginBottom: '34px' }}>
						{t('files.search_description')}
					</Typography>
					<Formik
						initialValues={{
							name: labelsDetails?.name || '',
							prompt: labelsDetails?.prompt || '',
						}}
						validationSchema={GenerativeLabelValidationSchema}
						onSubmit={handleManualModalSubmit}
					>
						<Form>
							<InputField
								name={'name'}
								label={t('files.labels.research')}
								placeholder={t('files.search_placeholder')}
								required
							/>

							<InputField
								name={'prompt'}
								label={t('files.labels.rule')}
								placeholder={t('files.rule_placeholder')}
								multiline
							/>

							<Button type={'submit'} fullWidth>
								{labelsDetails ? t('shared_texts.edit') : t('shared_texts.add')}
							</Button>
						</Form>
					</Formik>
				</>
			)}
		</Dialog>
	);
};

export default GenerativeLabelsModal;
